/**
 * DOS518 - Digital Operations System for Wildfire Management
 * 
 * Copyright (c) 2024-2025 Angelo Irtilaç
 * All rights reserved.
 * 
 * This software is proprietary and confidential.
 * Unauthorized copying, distribution, modification, or use is strictly prohibited.
 * 
 * ---
 * 
 * Author: Angelo Irtilaç
 * Qualifications: DOS (Direttore delle Operazioni di Spegnimento)
 *                39 years - Corpo Nazionale dei Vigili del Fuoco
 *                DOS Trainer & Instructor
 * 
 * Location: Carpi, Italy
 * Contact: [inserire email]
 * 
 * ---
 * 
 * This software implements official Italian Fire Department protocols:
 * - C.O.A.U. (Centro Operativo Aereo Unificato)
 * - SI.TA.C. (Sistema Tattico di Coordinamento)
 * - L.A.C.E.S. (Lookout, Awareness, Communication, Escape routes, Safety zones)
 * 
 * Developed with operational expertise acquired through decades of wildfire
 * emergency management and coordination of suppression operations.
 * 
 * For commercial licensing inquiries, please contact the author.
 */
// SITAC.tsx - Con FALLBACK automatico se geocoding fallisce
import React, { useState, useEffect } from 'react';
import { TacticalMap } from './TacticalMap';
import { useAppStore } from '../../store/useAppStore';
import type { PlacedSymbol } from '@/config/sitac';
import { usePosizioniCampo } from '../../hooks/usePosizioniCampo';


export const SITAC: React.FC = () => {
  const [placedSymbols, setPlacedSymbols] = useState<PlacedSymbol[]>([]);
  const [mapCoords, setMapCoords] = useState<{lat: number, lon: number}>({ lat: 44.7847, lon: 10.8791 }); // Default: Carpi
  const [innescoCoords, setInnescoCoords] = useState<[number, number] | null>(null); // null = nessun geocoding riuscito
  const [isLoadingMap, setIsLoadingMap] = useState(false);
  const [geoStatus, setGeoStatus] = useState<string>('📍 Carpi (default)');
  const [showBussola, setShowBussola] = useState(false);
  const [showCampoPanel, setShowCampoPanel] = useState(false);
  const [codiceManuale, setCodiceManuale] = useState(() => localStorage.getItem('dos518_codice_operazione') ?? '');

  // Store
  const coau = useAppStore(state => state.coau);
  const mezziAerei = useAppStore(state => state.mezziAerei);
  const mezziTerrestri = useAppStore(state => state.mezziTerrestri);

  // Codice operazione: da COAU id o manuale — persisto sempre in localStorage
  const codiceOperazione = (codiceManuale.trim() || (coau?.id ? coau.id.slice(0, 8).toUpperCase() : '')).toUpperCase() || undefined;
  useEffect(() => {
    if (codiceOperazione) localStorage.setItem('dos518_codice_operazione', codiceOperazione);
  }, [codiceOperazione]);

  // Tablet di campo — attivo automaticamente se c'è un codice operazione
  const [showPosizioniCampo, setShowPosizioniCampo] = useState(() => !!localStorage.getItem('dos518_codice_operazione'));
  const { posizioni: posizioniCampo, connesso: campConnesso, fetchError: campoFetchError } = usePosizioniCampo(
    showPosizioniCampo ? (codiceOperazione ?? null) : null
  );

  // Geocoding con Nominatim
  const geocodeAddress = async (address: string): Promise<{lat: number, lon: number} | null> => {
    try {
      console.log('🌍 Geocoding indirizzo:', address);
      const url = `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(address)}&limit=1`;
      
      const response = await fetch(url, {
        headers: {
          'User-Agent': 'DOS518-IRTILAC/1.0'
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      
      const data = await response.json();
      console.log('📍 Risultato geocoding:', data);
      
      if (data && Array.isArray(data) && data.length > 0) {
        return {
          lat: parseFloat(data[0].lat),
          lon: parseFloat(data[0].lon)
        };
      }
      return null;
    } catch (error) {
      console.error('❌ Errore geocoding:', error);
      return null;
    }
  };

  // Carica coordinate da COAU — tre priorità:
  //  1. latitudine/longitudine dirette (GPS drone o inserimento manuale)
  //  2. geocoding dell'indirizzo via Nominatim
  //  3. fallback Carpi (nessun dato COAU)
  useEffect(() => {
    const loadMap = async () => {

      // ── Priorità 1: coordinate GPS dirette nel COAU ──────────
      const latRaw = coau?.latitudine;
      const lonRaw = coau?.longitudine;
      if (latRaw && lonRaw) {
        const lat = parseFloat(String(latRaw).replace(',', '.'));
        const lon = parseFloat(String(lonRaw).replace(',', '.'));
        if (!isNaN(lat) && !isNaN(lon) && Math.abs(lat) <= 90 && Math.abs(lon) <= 180) {
          setMapCoords({ lat, lon });
          setInnescoCoords([lat, lon]);
          setGeoStatus(`📍 Coordinate (${lat.toFixed(4)}, ${lon.toFixed(4)})`);
          return;
        }
      }

      // ── Priorità 2: geocoding indirizzo ──────────────────────
      const indirizzo = coau?.indirizzo;
      if (!indirizzo || indirizzo.trim().length < 3) {
        setGeoStatus('📍 Carpi (nessun dato COAU)');
        setMapCoords({ lat: 44.7847, lon: 10.8791 });
        return;
      }

      setIsLoadingMap(true);
      setGeoStatus(`🔍 Geocoding: ${indirizzo}…`);
      const coords = await geocodeAddress(indirizzo);

      if (coords) {
        setMapCoords(coords);
        setInnescoCoords([coords.lat, coords.lon]);
        setGeoStatus(`✅ ${indirizzo} (${coords.lat.toFixed(4)}, ${coords.lon.toFixed(4)})`);
      } else {
        setGeoStatus(`⚠️ "${indirizzo}" non trovato — uso Carpi`);
        setMapCoords({ lat: 44.7847, lon: 10.8791 });
      }
      setIsLoadingMap(false);
    };

    loadMap();
  }, [coau?.indirizzo, coau?.latitudine, coau?.longitudine]);

  // Forza caricamento manuale
  const handleForceLoad = async () => {
    const indirizzo = prompt('Inserisci indirizzo:', 
      coau?.indirizzo || 'Carpi, MO');
    
    if (!indirizzo) return;

    setIsLoadingMap(true);
    setGeoStatus(`🔍 Cercando: ${indirizzo}...`);
    
    const coords = await geocodeAddress(indirizzo);
    
    if (coords) {
      setMapCoords(coords);
      setInnescoCoords([coords.lat, coords.lon]);
      setGeoStatus(`✅ ${indirizzo} (${coords.lat.toFixed(4)}, ${coords.lon.toFixed(4)})`);
    } else {
      alert(`❌ Impossibile trovare "${indirizzo}".\nVerifica l'indirizzo e riprova.`);
      setGeoStatus(`❌ "${indirizzo}" non trovato`);
    }
    
    setIsLoadingMap(false);
  };

  // Usa coordinate correnti
  const handleUseCurrentLocation = () => {
    if ("geolocation" in navigator) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const lat = position.coords.latitude;
          const lon = position.coords.longitude;
          setMapCoords({ lat, lon });
          setInnescoCoords([lat, lon]);
          setGeoStatus(`📍 Posizione GPS (${lat.toFixed(4)}, ${lon.toFixed(4)})`);
        },
        (error) => {
          alert('❌ Impossibile ottenere posizione GPS');
          console.error('GPS error:', error);
        }
      );
    } else {
      alert('❌ Geolocalizzazione non supportata dal browser');
    }
  };

  return (
    <div style={{ height: 'calc(100vh - 70px)', overflow: 'hidden', position: 'relative' }}>
      <TacticalMap
        initialCenter={{ lat: mapCoords.lat, lng: mapCoords.lon }}
        initialInnesco={innescoCoords}
        initialZoom={15}
        initialSymbols={placedSymbols}
        onSymbolsChange={setPlacedSymbols}
        showGrid={true}
        height="100%"
        geoStatus={geoStatus}
        onGPS={handleUseCurrentLocation}
        codiceOperazione={codiceOperazione}
        posizioniCampo={posizioniCampo}
        campConnesso={campConnesso}
        showPosizioniCampo={showPosizioniCampo}
        onTogglePosizioniCampo={() => setShowPosizioniCampo(v => !v)}
        resourceCounts={{
          aerei:      Math.max(0, mezziAerei.length     - placedSymbols.filter(s => s.symbol.categoria === 'dispositivo_aereo').length),
          terrestri:  Math.max(0, mezziTerrestri.length - placedSymbols.filter(s => s.symbol.categoria === 'dispositivo_terrestre').length),
          simboli:    placedSymbols.length,
        }}
      />

      {/* Pannello condivisione tablet di campo */}
      <button
        onClick={() => setShowCampoPanel(true)}
        style={{
          position: 'absolute', bottom: 30, left: 100,
          width: 60, height: 60,
          background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
          border: '3px solid #22c55e', borderRadius: '50%',
          fontSize: '1.6rem', cursor: 'pointer',
          boxShadow: '0 4px 12px rgba(34,197,94,0.4)',
          zIndex: 500,
          touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent',
        }}
        title="Condividi con tablet di campo"
      >📡</button>

      {showCampoPanel && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 }}
          onClick={() => setShowCampoPanel(false)}
        >
          <div
            style={{ background: '#1e293b', borderRadius: 16, padding: 28, maxWidth: 440, width: '95%', boxShadow: '0 8px 40px rgba(0,0,0,0.6)' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h3 style={{ color: '#f1f5f9', margin: 0, fontWeight: 800 }}>📡 Tablet di Campo</h3>
              <button onClick={() => setShowCampoPanel(false)} style={{ background: 'none', border: 'none', color: '#94a3b8', fontSize: 22, cursor: 'pointer' }}>✕</button>
            </div>

            <p style={{ color: '#94a3b8', fontSize: '0.85rem', marginBottom: 16 }}>
              I tablet di campo aprono questo URL e condividono la loro posizione GPS in real-time sulla mappa SITAC.
            </p>

            <label style={{ display: 'block', color: '#64748b', fontSize: '0.78rem', fontWeight: 600, marginBottom: 6 }}>
              Codice operazione (modificabile)
            </label>
            <input
              style={{ width: '100%', padding: '10px 12px', background: '#0f172a', border: '1px solid #334155', borderRadius: 8, color: '#f1f5f9', fontSize: '0.95rem', marginBottom: 16, boxSizing: 'border-box', fontFamily: 'monospace', letterSpacing: 2 }}
              value={codiceManuale || (coau?.id ? coau.id.slice(0, 8).toUpperCase() : '')}
              onChange={(e) => { const v = e.target.value.toUpperCase(); setCodiceManuale(v); localStorage.setItem('dos518_codice_operazione', v); }}
              placeholder="es. DOS-001"
            />

            {codiceOperazione ? (
              <>
                <label style={{ display: 'block', color: '#64748b', fontSize: '0.78rem', fontWeight: 600, marginBottom: 6 }}>
                  URL da condividere con i tablet di campo
                </label>
                <div style={{ background: '#0f172a', border: '1px solid #334155', borderRadius: 8, padding: '10px 14px', marginBottom: 16, wordBreak: 'break-all' }}>
                  <span style={{ color: '#22c55e', fontSize: '0.82rem', fontFamily: 'monospace' }}>
                    {(window.location.hostname === 'localhost' || window.location.protocol === 'capacitor:' ? 'https://dos518.pages.dev' : window.location.origin)}/?campo={codiceOperazione}
                  </span>
                </div>
                <button
                  style={{ width: '100%', padding: 12, background: '#22c55e', border: 'none', borderRadius: 10, color: '#fff', fontWeight: 800, fontSize: '0.9rem', cursor: 'pointer', marginBottom: 10 }}
                  onClick={() => { const origin = (window.location.hostname === 'localhost' || window.location.protocol === 'capacitor:') ? 'https://dos518.pages.dev' : window.location.origin; navigator.clipboard?.writeText(`${origin}/?campo=${codiceOperazione}`); }}
                >
                  📋 Copia URL
                </button>
                <button
                  style={{ width: '100%', padding: 12, background: showPosizioniCampo ? '#15803d' : '#1e40af', border: 'none', borderRadius: 10, color: '#fff', fontWeight: 800, fontSize: '0.9rem', cursor: 'pointer', marginBottom: 6 }}
                  onClick={() => setShowPosizioniCampo(v => !v)}
                >
                  {showPosizioniCampo ? '📡 Monitoraggio campo ATTIVO' : '📡 Attiva monitoraggio campo'}
                </button>
                <div style={{ background: '#0f172a', border: '1px solid #334155', borderRadius: 8, padding: '10px 14px', marginTop: 4, fontSize: '0.8rem' }}>
                  <div style={{ color: '#64748b', marginBottom: 4 }}>Codice: <span style={{ color: '#f1f5f9', fontFamily: 'monospace' }}>{codiceOperazione ?? '—'}</span></div>
                  <div style={{ color: '#64748b' }}>Posizioni ricevute: <span style={{ color: posizioniCampo.length > 0 ? '#22c55e' : '#f59e0b', fontWeight: 700 }}>{posizioniCampo.length}</span></div>
                  <div style={{ color: '#64748b' }}>Realtime: <span style={{ color: campConnesso ? '#22c55e' : '#f59e0b' }}>{campConnesso ? '● connesso' : '○ polling'}</span></div>
                  {campoFetchError && <div style={{ color: '#ef4444', marginTop: 4, fontSize: '0.75rem', wordBreak: 'break-all' }}>⚠️ {campoFetchError}</div>}
                </div>
              </>
            ) : (
              <p style={{ color: '#f59e0b', fontSize: '0.85rem' }}>
                Compila il COAU o inserisci un codice manuale per generare l'URL.
              </p>
            )}
          </div>
        </div>
      )}

      {/* Pulsante bussola fisso in basso a sinistra */}
      <button
        onClick={() => setShowBussola(true)}
        style={{
          position: 'absolute',
          bottom: 30,
          left: 30,
          width: 60,
          height: 60,
          background: 'linear-gradient(135deg, #ff6b35 0%, #ff8c42 100%)',
          border: '3px solid #fff',
          borderRadius: '50%',
          fontSize: '2rem',
          cursor: 'pointer',
          boxShadow: '0 4px 12px rgba(255,107,53,0.6)',
          zIndex: 500,
          touchAction: 'manipulation',
          WebkitTapHighlightColor: 'transparent',
        }}
        title="Apri bussola"
      >
        🧭
      </button>

      {/* Modale bussola */}
      {showBussola && (
        <div
          style={{
            position: 'fixed', inset: 0,
            background: 'rgba(0,0,0,0.85)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 2000,
          }}
          onClick={() => setShowBussola(false)}
        >
          <div
            style={{
              background: 'linear-gradient(135deg, #1a1a1a, #2d2d2d)',
              borderRadius: 12,
              border: '2px solid #ff6b35',
              maxWidth: 500,
              width: '95%',
              padding: '20px 25px',
              boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h3 style={{ color: '#fff', margin: 0, fontWeight: 900 }}>🧭 Bussola</h3>
              <button
                onClick={() => setShowBussola(false)}
                style={{
                  background: 'rgba(255,255,255,0.2)', border: 'none', color: '#fff',
                  fontSize: 24, cursor: 'pointer', padding: '5px 12px', borderRadius: 6,
                  minWidth: 44, minHeight: 44, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent',
                }}
              >✕</button>
            </div>

            <div style={{ display: 'flex', justifyContent: 'center', padding: '20px 0' }}>
              <div style={{
                position: 'relative', width: 280, height: 280,
                borderRadius: '50%', border: '4px solid #ff6b35',
                background: 'radial-gradient(circle, #1a1a1a 0%, #0a0a0a 100%)',
                boxShadow: '0 0 20px rgba(255,107,53,0.3)',
              }}>
                <div style={{ position: 'absolute', top: 10, left: '50%', transform: 'translateX(-50%)', color: '#ff6b35', fontWeight: 900, fontSize: '2rem' }}>N</div>
                <div style={{ position: 'absolute', right: 20, top: '50%', transform: 'translateY(-50%)', color: '#fff', fontWeight: 900, fontSize: '1.5rem' }}>E</div>
                <div style={{ position: 'absolute', bottom: 10, left: '50%', transform: 'translateX(-50%)', color: '#fff', fontWeight: 900, fontSize: '1.5rem' }}>S</div>
                <div style={{ position: 'absolute', left: 20, top: '50%', transform: 'translateY(-50%)', color: '#fff', fontWeight: 900, fontSize: '1.5rem' }}>O</div>
                <div style={{ position: 'absolute', top: 28, right: 28, color: '#999', fontWeight: 900, fontSize: '1rem' }}>NE</div>
                <div style={{ position: 'absolute', bottom: 28, right: 28, color: '#999', fontWeight: 900, fontSize: '1rem' }}>SE</div>
                <div style={{ position: 'absolute', bottom: 28, left: 28, color: '#999', fontWeight: 900, fontSize: '1rem' }}>SO</div>
                <div style={{ position: 'absolute', top: 28, left: 28, color: '#999', fontWeight: 900, fontSize: '1rem' }}>NO</div>
                <div style={{
                  position: 'absolute', top: '50%', left: '50%',
                  width: 4, height: '40%',
                  background: 'linear-gradient(to bottom, #ff6b35 0%, #ff6b35 50%, #fff 50%, #fff 100%)',
                  transformOrigin: 'bottom center',
                  transform: 'translate(-50%, -100%)',
                  borderRadius: 2,
                  boxShadow: '0 0 10px rgba(255,107,53,0.8)',
                }} />
              </div>
            </div>

            <div style={{
              display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10,
              background: '#0a0a0a', border: '1px solid #333', borderRadius: 8, padding: 15,
            }}>
              <div style={{ color: '#aaa', fontSize: '0.9rem' }}><strong style={{ color: '#ff6b35' }}>N</strong> - Nord (0°)</div>
              <div style={{ color: '#aaa', fontSize: '0.9rem' }}><strong style={{ color: '#ff6b35' }}>E</strong> - Est (90°)</div>
              <div style={{ color: '#aaa', fontSize: '0.9rem' }}><strong style={{ color: '#ff6b35' }}>S</strong> - Sud (180°)</div>
              <div style={{ color: '#aaa', fontSize: '0.9rem' }}><strong style={{ color: '#ff6b35' }}>O</strong> - Ovest (270°)</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
